/** Flashbang throw FX — arc + victim white-out. */

import { ARENA_CX, ARENA_CY, ARENA_W, type Role } from "@duelgame/shared";
import {
  FLASHBANG_FADE_MS,
  FLASHBANG_FLIGHT_MS,
  FLASHBANG_HOLD_MS,
} from "./abilities";

export interface FlashbangThrow {
  thrower: Role;
  startMs: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

/** Landing spot: middle of the opponent's half. */
export function flashbangTarget(thrower: Role): { x: number; y: number } {
  return {
    x: thrower === "host" ? (ARENA_CX + ARENA_W) / 2 : ARENA_CX / 2,
    y: ARENA_CY,
  };
}

/** Position along the throw arc, or null if not in flight. */
export function flashbangPos(
  fb: FlashbangThrow,
  now: number
): { x: number; y: number; angle: number } | null {
  const t = (now - fb.startMs) / FLASHBANG_FLIGHT_MS;
  if (t < 0 || t > 1) return null;
  const x = fb.fromX + (fb.toX - fb.fromX) * t;
  const y =
    fb.fromY + (fb.toY - fb.fromY) * t - Math.sin(Math.PI * t) * 90;
  const angle = Math.atan2(fb.toY - fb.fromY, fb.toX - fb.fromX) + t * 4;
  return { x, y, angle };
}

/**
 * White overlay alpha for a local player blinded by an opponent's flashbang.
 * Full white for FLASHBANG_HOLD_MS, then fades over FLASHBANG_FADE_MS.
 */
export function flashbangWhiteAlpha(
  throws: readonly FlashbangThrow[],
  localRole: Role,
  now: number
): number {
  let max = 0;
  for (const fb of throws) {
    if (fb.thrower === localRole) continue;
    const elapsed = now - fb.startMs;
    if (elapsed < FLASHBANG_FLIGHT_MS) continue;
    const afterBang = elapsed - FLASHBANG_FLIGHT_MS;
    if (afterBang < FLASHBANG_HOLD_MS) {
      max = 1;
      continue;
    }
    const fadeT = (afterBang - FLASHBANG_HOLD_MS) / FLASHBANG_FADE_MS;
    if (fadeT >= 1) continue;
    max = Math.max(max, 1 - fadeT);
  }
  return max;
}
